import {
  ActivateCmpMemberMutation,
  ChamberMemberReturn,
  CmpMemberActionInput,
  CmpMemberLifecycleStatusEnum,
  GetChamberMemberQuery,
} from "../../../graphql/types";
import { chamberMemberSuspensionUpdateMatchesRequest } from "./memberFormUtils";
import { UserFacingError } from "../../common/utilities/userFacingError";

export type MemberStatusRecord = Pick<
  GetChamberMemberQuery["getChamberMember"],
  "id" | "chamberId" | "isSuspended" | "lifecycleStatus" | "cmpExtension"
>;

type MemberLifecycleFields = Pick<MemberStatusRecord, "isSuspended" | "lifecycleStatus">;

export const getMemberLifecycleStatus = (member: MemberLifecycleFields) =>
  member.lifecycleStatus || (member.isSuspended
    ? CmpMemberLifecycleStatusEnum.Suspended
    : CmpMemberLifecycleStatusEnum.Active);

export const getMemberStatusLabel = (
  member: MemberStatusRecord,
  labels?: { active: string; suspended: string }
) => {
  const status = getMemberLifecycleStatus(member);
  if (labels && status === CmpMemberLifecycleStatusEnum.Active) return labels.active;
  if (labels && status === CmpMemberLifecycleStatusEnum.Suspended) return labels.suspended;
  return status.charAt(0) + status.slice(1).toLowerCase();
};

// The backend issues Member Dashboard credentials only to Active members.
export const memberCredentialsRequireActiveMessage =
  "Member credentials are available only when the member status is Active.";

export const canIssueMemberCredentials = (member: MemberLifecycleFields) =>
  !member.isSuspended &&
  getMemberLifecycleStatus(member) === CmpMemberLifecycleStatusEnum.Active;

export const canChangeMemberSuspension = (member: MemberStatusRecord) => {
  const status = getMemberLifecycleStatus(member);
  return status === CmpMemberLifecycleStatusEnum.Active ||
    status === CmpMemberLifecycleStatusEnum.Suspended;
};

type LifecycleAction = (
  input: CmpMemberActionInput
) => Promise<ActivateCmpMemberMutation["activateCmpMember"] | null | undefined>;

export type MemberStatusUpdate = {
  member: MemberStatusRecord;
  requestedIsSuspended: boolean;
  updateLegacyMember: () => Promise<Pick<ChamberMemberReturn, "isSuspended"> | null | undefined>;
};

/** Keep legacy profile writes separate from CMP's audited lifecycle actions. */
export const updateMemberSuspension = async ({
  member,
  requestedIsSuspended,
  updateLegacyMember,
  activateMember,
  suspendMember,
}: MemberStatusUpdate & {
  activateMember: LifecycleAction;
  suspendMember: LifecycleAction;
}): Promise<void> => {
  const requestedStatus = requestedIsSuspended
    ? CmpMemberLifecycleStatusEnum.Suspended
    : CmpMemberLifecycleStatusEnum.Active;
  const currentStatus = getMemberLifecycleStatus(member);

  if (member.cmpExtension || member.lifecycleStatus) {
    if (currentStatus === requestedStatus) return;
    if (!canChangeMemberSuspension(member)) {
      // Saving a profile must not turn an unchanged Pending or Past flag
      // into a request to bypass that membership workflow.
      if (requestedIsSuspended === (member.isSuspended === true)) return;
      throw new UserFacingError(`This member's status is ${getMemberStatusLabel(member)}. You cannot change it to Active or Suspended here. Open this member in Chamber Member Pro and change the membership there.`);
    }
    const result = await (requestedIsSuspended ? suspendMember : activateMember)({
      chamberId: member.chamberId,
      memberId: member.id,
    });
    if (!result?.actionSuccessful ||
      result.detail.lifecycleStatus !== requestedStatus ||
      result.detail.member.id !== member.id ||
      result.detail.member.chamberId !== member.chamberId ||
      result.detail.member.isSuspended !== requestedIsSuspended) {
      throw new UserFacingError("We could not confirm the new member status. Refresh this page and check the status before you try again.");
    }
    return;
  }

  const result = await updateLegacyMember();
  if (!chamberMemberSuspensionUpdateMatchesRequest({
    requestedIsSuspended,
    returnedIsSuspended: result?.isSuspended,
  })) {
    throw new UserFacingError("We could not confirm the new member status. Refresh this page and check the status before you try again.");
  }
};
